// Mirror of packages/shared/src/common/alert-severity.js (studio cannot import @nolan/shared).

const CRITICAL_SCENARIOS = new Set(['export', 'report_job', 'load', 'render'])
const API_OWNED_SCENARIOS = new Set(['export', 'report_job'])
const STUCK_REASONS = new Set(['stuck', 'job_stuck'])
const BROWSER_PLATFORM = 'javascript'
// Firebase Auth reports its own drops as `Firebase: Error (auth/network-request-failed).`
const NETWORK_FAILURE = /(?:^|: )(?:Network Error|Failed to fetch|Load failed|NetworkError when attempting to fetch resource)|\(auth\/network-request-failed\)/
export const LOGGER_SOURCE = 'logger'
// Studio's page for headless renderers (the pitch-deck PDF Lambda reports its own failures, with the user).
export const HEADLESS_RENDER_SURFACE = 'headless_render'
const SEPARATOR = ' · '

export const PLAN_LABELS = Object.freeze({ free: 'Free', basic: 'Basic', basicplus: 'Basic Plus', pro: 'Pro' })

export const FEATURE_LABELS = Object.freeze({
  editor: 'Script Editor',
  breakdown: 'Script Breakdown',
  scheduling: 'Scheduling',
  budgeting: 'Budgeting',
  shotList: 'Shot List',
  aiVideo: 'AI Video',
  storyboard: 'Storyboard',
  pitchDeck: 'Pitch Deck',
  plotHole: 'Plot Hole',
  scriptCoverage: 'Script Coverage',
  tableRead: 'Table Read',
  scriptDoctor: 'Script Doctor',
  characterDevelopment: 'Character Development',
  projectCreation: 'Project Creation',
  project: 'Project'
})

const own = (map, key) => Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined

export const featureLabel = (feature) => own(FEATURE_LABELS, feature) || 'Studio'

export const failurePhrase = ({ scenario, export_type: exportType, reason } = {}, platform) => {
  const outcome = STUCK_REASONS.has(reason) ? 'stuck' : 'failed'
  if (scenario === 'export') return `${exportType ? `${String(exportType).toUpperCase()} ` : ''}export ${outcome}`
  if (scenario === 'report_job') return `report ${outcome}`
  if (scenario === 'load') return 'failed to open'
  return platform === BROWSER_PLATFORM ? 'screen crashed' : 'render failed'
}

export const planLabel = ({ plan, customer_type: customerType } = {}) => {
  const name = plan ? own(PLAN_LABELS, String(plan).toLowerCase()) || String(plan).charAt(0).toUpperCase() + String(plan).slice(1) : 'Free'
  return customerType === 'trial' ? `${name} trial` : name
}

const topValue = (event) => {
  const values = event.exception?.values
  return String((values?.length ? values[values.length - 1].value : event.message) || '')
}

// No response at all — the user's connection dropped or the page reloaded mid-request.
const isNetworkDrop = (event, tags) =>
  event.platform === BROWSER_PLATFORM && tags.http_status == null && NETWORK_FAILURE.test(topValue(event))

// A 500 the API reports itself (with the server stack).
const reportedElsewhere = (event, tags) =>
  event.platform === BROWSER_PLATFORM && String(tags.http_status) === '500' && API_OWNED_SCENARIOS.has(tags.scenario)

export const severityOf = (event) => {
  const tags = event.tags || {}
  const level = event.level || 'error'
  const fromLogger = tags.source === LOGGER_SOURCE
  if ((fromLogger && level === 'warning') || isNetworkDrop(event, tags)) return 'silent'
  const userFacing = CRITICAL_SCENARIOS.has(tags.scenario) && !fromLogger && tags.job_outcome !== 'retrying' &&
    tags.surface !== HEADLESS_RENDER_SURFACE && !reportedElsewhere(event, tags)
  if (userFacing && (level === 'error' || level === 'fatal')) return 'critical'
  return level === 'warning' ? 'warning' : 'error'
}

const withPrefix = (text, head) => {
  const original = text == null ? '' : String(text)
  if (!original || original === head) return head
  return original.startsWith(`${head}${SEPARATOR}`) ? original : `${head}${SEPARATOR}${original}`
}

// Sentry titles an issue "<type>: <value>" from the last (top-level) exception; linked causes come first.
const retitle = (event) => {
  const tags = event.tags
  const type = `${featureLabel(tags.feature)} ${failurePhrase(tags, event.platform)}`
  const head = `${event.user?.email || tags.user_email || 'Unknown user'} (${planLabel(tags)})`
  const values = event.exception?.values
  if (values?.length) {
    const top = values[values.length - 1]
    top.type = type
    top.value = withPrefix(top.value, head)
    return
  }
  const title = `${type}: `
  const message = event.message == null ? '' : String(event.message)
  event.message = `${title}${withPrefix(message.startsWith(title) ? message.slice(title.length) : message, head)}`
}

export const classifyAlert = (event) => {
  if (!event) return event
  try {
    const severity = severityOf(event)
    event.tags = { ...event.tags, severity }
    // Silent one at a time; the network-drop alert counts the users they hit.
    if (isNetworkDrop(event, event.tags)) event.tags.network_drop = 'yes'
    if (severity === 'critical') {
      const { feature, scenario, user_email: userEmail } = event.tags
      event.level = 'fatal'
      event.fingerprint = ['critical', feature || 'none', scenario, String(event.user?.id || userEmail || 'anonymous')]
      retitle(event)
    }
  } catch (_) {
    // Classifying must never drop the event.
  }
  return event
}
